import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash, Check, X, Table } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};
const INP = {
  width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box',
};
const MONO_SM = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em',
};

const UOM_CATEGORIES = ['general', 'count', 'weight', 'volume', 'length', 'area'];

// ── Generic CRUD table ────────────────────────────────────────────────────────

function MasterTable({ title, items, loading, onAdd, onEdit, onDelete, columns, AddForm, EditForm }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  return (
    <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid hsl(0,0%,12%)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,55%)',
          textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
          {items.length} {title}
        </span>
        <button data-testid={`add-${title.toLowerCase().replace(/\s+/g,'-')}-btn`}
          onClick={() => setShowAdd(true)} className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 10 }}>
          <Plus size={12} weight="bold" /> Add
        </button>
      </div>
      <div className="table-scroll">
        <table className="erp-table" style={{ width: '100%' }}>
          <thead>
            <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}<th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} style={{ padding: 20, color: 'hsl(0,0%,40%)' }}>Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} style={{ padding: 20, textAlign: 'center', color: 'hsl(0,0%,35%)' }}>No entries — click Add</td></tr>
            ) : items.map(item => (
              <tr key={item.id} data-testid={`master-row-${item.code || item.id}`}>
                {columns.map(c => (
                  <td key={c.key} style={c.style || {}}>
                    {c.render ? c.render(item[c.key], item) : item[c.key] || '—'}
                  </td>
                ))}
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {deletingId === item.id ? (
                      <>
                        <button onClick={() => { onDelete(item.id); setDeletingId(null); }}
                          style={{ padding: '3px 8px', background: 'hsl(0,60%,30%)', border: 'none',
                            color: 'hsl(0,84%,70%)', fontSize: 9, cursor: 'pointer',
                            fontFamily: "'JetBrains Mono', monospace" }}>
                          CONFIRM
                        </button>
                        <button onClick={() => setDeletingId(null)}
                          style={{ padding: '3px 8px', background: 'transparent', border: '1px solid hsl(0,0%,22%)',
                            color: 'hsl(0,0%,50%)', fontSize: 9, cursor: 'pointer',
                            fontFamily: "'JetBrains Mono', monospace" }}>
                          CANCEL
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditItem(item)} data-testid={`edit-master-${item.id}`}
                          style={{ padding: '3px 8px', background: 'transparent', border: '1px solid hsl(0,0%,20%)',
                            color: 'hsl(197,100%,48%)', fontSize: 9, cursor: 'pointer',
                            fontFamily: "'JetBrains Mono', monospace', display: 'flex', alignItems: 'center', gap: 4" }}>
                          <Pencil size={10} />
                        </button>
                        <button onClick={() => setDeletingId(item.id)} data-testid={`delete-master-${item.id}`}
                          style={{ padding: '3px 8px', background: 'transparent', border: '1px solid hsl(0,0%,20%)',
                            color: 'hsl(0,84%,55%)', fontSize: 9, cursor: 'pointer',
                            fontFamily: "'JetBrains Mono', monospace" }}>
                          <Trash size={10} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <FormModal title={`Add ${title}`} onClose={() => setShowAdd(false)}
          onSave={(data) => { onAdd(data); setShowAdd(false); }}>
          <AddForm />
        </FormModal>
      )}

      {/* Edit Modal */}
      {editItem && (
        <FormModal title={`Edit ${title}`} onClose={() => setEditItem(null)}
          onSave={(data) => { onEdit(editItem.id, data); setEditItem(null); }}>
          <EditForm item={editItem} />
        </FormModal>
      )}
    </div>
  );
}

function FormModal({ title, onClose, onSave, children }) {
  const [formData, setFormData] = useState({});
  const childWithState = {
    ...children,
    props: { ...children.props, formData, setFormData },
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 480, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 14,
            color: 'hsl(0,0%,95%)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={14} /></button>
        </div>
        {childWithState}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} className="btn-ghost" style={{ fontSize: 11 }}>Cancel</button>
          <button onClick={() => onSave(formData)} className="btn-primary"
            style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Check size={12} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── UOM Forms ─────────────────────────────────────────────────────────────────

function UOMAddForm({ formData, setFormData }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <label style={LBL}>Code * (e.g. ml, kg, pcs)</label>
        <input data-testid="uom-code" value={formData.code || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, code: e.target.value }))} placeholder="ml" />
      </div>
      <div>
        <label style={LBL}>Name *</label>
        <input data-testid="uom-name" value={formData.name || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Millilitres" />
      </div>
      <div>
        <label style={LBL}>Category</label>
        <select data-testid="uom-category" value={formData.category || 'general'}
          onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
          style={{ ...INP, appearance: 'auto' }}>
          {UOM_CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>
    </div>
  );
}

function UOMEditForm({ item, formData, setFormData }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setFormData({ code: item.code, name: item.name, category: item.category || 'general' });
  }, [item.id]);
  return <UOMAddForm formData={formData} setFormData={setFormData} />;
}

// ── Product Category Forms ────────────────────────────────────────────────────

function ProdCatAddForm({ formData, setFormData }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <label style={LBL}>Code * (e.g. WBM, BP)</label>
        <input data-testid="pcat-code" value={formData.code || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="WBM" />
      </div>
      <div>
        <label style={LBL}>Name *</label>
        <input data-testid="pcat-name" value={formData.name || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Whiteboard Marker" />
      </div>
      <div>
        <label style={LBL}>Description</label>
        <input data-testid="pcat-desc" value={formData.description || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Optional description" />
      </div>
      <div>
        <label style={LBL}>HSN Code</label>
        <input data-testid="prod-cat-hsn-input" value={formData.hsn_code || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, hsn_code: e.target.value }))} placeholder="HSN Code (e.g. 32082090)" />
      </div>
      <div>
        <label style={LBL}>GST Rate %</label>
        <input data-testid="prod-cat-gst-input" type="number" value={formData.gst_rate ?? ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, gst_rate: e.target.value }))} placeholder="GST Rate % (e.g. 18)" />
      </div>
    </div>
  );
}

function ProdCatEditForm({ item, formData, setFormData }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setFormData({ code: item.code, name: item.name, description: item.description || '', hsn_code: item.hsn_code || '', gst_rate: item.gst_rate ?? '' });
  }, [item.id]);
  return <ProdCatAddForm formData={formData} setFormData={setFormData} />;
}

// ── Vendor Category Forms ─────────────────────────────────────────────────────

function VendCatAddForm({ formData, setFormData }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <label style={LBL}>Code * (e.g. raw_material)</label>
        <input data-testid="vcat-code" value={formData.code || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, code: e.target.value.toLowerCase().replace(/\s+/g,'_') }))}
          placeholder="raw_material" />
      </div>
      <div>
        <label style={LBL}>Name *</label>
        <input data-testid="vcat-name" value={formData.name || ''} style={INP}
          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Raw Material" />
      </div>
    </div>
  );
}

function VendCatEditForm({ item, formData, setFormData }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setFormData({ code: item.code, name: item.name });
  }, [item.id]);
  return <VendCatAddForm formData={formData} setFormData={setFormData} />;
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MasterTables() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('uoms');
  const [uoms, setUoms] = useState([]);
  const [prodCats, setProdCats] = useState([]);
  const [vendCats, setVendCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const isAdmin = user?.role === 'admin';

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [uR, pR, vR] = await Promise.all([
        api.get('/masters/uoms'),
        api.get('/masters/product-categories'),
        api.get('/masters/vendor-categories'),
      ]);
      setUoms(uR.data);
      setProdCats(pR.data);
      setVendCats(vR.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (endpoint, data, setter) => {
    if (!isAdmin) { showToast('Admin only', 'error'); return; }
    try {
      const r = await api.post(endpoint, data);
      setter(p => [...p, r.data]);
      showToast('Created successfully');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to create', 'error');
    }
  };

  const handleEdit = async (endpoint, id, data, setter) => {
    if (!isAdmin) { showToast('Admin only', 'error'); return; }
    try {
      const r = await api.put(`${endpoint}/${id}`, data);
      setter(p => p.map(x => x.id === id ? r.data : x));
      showToast('Updated successfully');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to update', 'error');
    }
  };

  const handleDelete = async (endpoint, id, setter) => {
    if (!isAdmin) { showToast('Admin only', 'error'); return; }
    try {
      await api.delete(`${endpoint}/${id}`);
      setter(p => p.filter(x => x.id !== id));
      showToast('Deleted');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to delete', 'error');
    }
  };

  const TABS = [
    { key: 'uoms', label: 'Units of Measure (UOM)' },
    { key: 'prodcat', label: 'Product Categories' },
    { key: 'vendcat', label: 'Vendor Categories' },
  ];

  const TAB_STY = (active) => ({
    padding: '8px 16px', background: active ? 'hsl(197,100%,48%)' : 'transparent',
    color: active ? '#000' : 'hsl(0,0%,50%)', border: 'none', cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.1em', transition: 'all 0.15s',
    borderBottom: active ? '2px solid hsl(197,100%,48%)' : '2px solid transparent',
  });

  return (
    <div data-testid="master-tables-page">
      {/* Toast */}
      {toast && (
        <div data-testid="master-toast" style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '10px 18px', background: toast.type === 'error' ? 'hsl(0,75%,40%)' : 'hsl(142,65%,38%)',
          color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          letterSpacing: '0.05em', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Master Tables</h1>
          <div style={MONO_SM}>UOMs · Product Categories · Vendor Categories</div>
        </div>
        {!isAdmin && (
          <div style={{ padding: '6px 12px', background: 'hsl(43,80%,10%)', border: '1px solid hsl(43,80%,30%)',
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(43,96%,56%)' }}>
            VIEW ONLY — Admin required for changes
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid hsl(0,0%,14%)', marginBottom: 16, display: 'flex' }}>
        {TABS.map(t => (
          <button key={t.key} style={TAB_STY(activeTab === t.key)}
            onClick={() => setActiveTab(t.key)} data-testid={`masters-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* UOMs */}
      {activeTab === 'uoms' && (
        <MasterTable
          title="UOMs"
          items={uoms}
          loading={loading}
          columns={[
            { key: 'code', label: 'Code', style: { color: 'hsl(197,100%,48%)', fontWeight: 700 } },
            { key: 'name', label: 'Name' },
            { key: 'category', label: 'Category', render: v => (
              <span style={{ padding: '2px 6px', background: 'hsl(0,0%,9%)', border: '1px solid hsl(0,0%,16%)',
                fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase' }}>
                {v}
              </span>
            )},
          ]}
          onAdd={(data) => handleAdd('/masters/uoms', data, setUoms)}
          onEdit={(id, data) => handleEdit('/masters/uoms', id, data, setUoms)}
          onDelete={(id) => handleDelete('/masters/uoms', id, setUoms)}
          AddForm={UOMAddForm}
          EditForm={UOMEditForm}
        />
      )}

      {/* Product Categories */}
      {activeTab === 'prodcat' && (
        <MasterTable
          title="Product Categories"
          items={prodCats}
          loading={loading}
          columns={[
            { key: 'code', label: 'Code', style: { color: 'hsl(197,100%,48%)', fontWeight: 700 } },
            { key: 'name', label: 'Category Name' },
            { key: 'description', label: 'Description', style: { color: 'hsl(0,0%,50%)', fontSize: 10 } },
            { key: 'hsn_code', label: 'HSN Code', render: (v) => v || '—' },
            { key: 'gst_rate', label: 'GST %', render: (v) => v != null && v !== '' ? v + '%' : '—' },
          ]}
          onAdd={(data) => handleAdd('/masters/product-categories', data, setProdCats)}
          onEdit={(id, data) => handleEdit('/masters/product-categories', id, data, setProdCats)}
          onDelete={(id) => handleDelete('/masters/product-categories', id, setProdCats)}
          AddForm={ProdCatAddForm}
          EditForm={ProdCatEditForm}
        />
      )}

      {/* Vendor Categories */}
      {activeTab === 'vendcat' && (
        <MasterTable
          title="Vendor Categories"
          items={vendCats}
          loading={loading}
          columns={[
            { key: 'code', label: 'Code', style: { color: 'hsl(197,100%,48%)', fontWeight: 700 } },
            { key: 'name', label: 'Category Name' },
          ]}
          onAdd={(data) => handleAdd('/masters/vendor-categories', data, setVendCats)}
          onEdit={(id, data) => handleEdit('/masters/vendor-categories', id, data, setVendCats)}
          onDelete={(id) => handleDelete('/masters/vendor-categories', id, setVendCats)}
          AddForm={VendCatAddForm}
          EditForm={VendCatEditForm}
        />
      )}
    </div>
  );
}
